/**
 * Related Products JS — Drag-to-scroll carousel with momentum + progress bar
 */
(function () {
  'use strict';

  var scrollEl = document.querySelector('[data-related-scroll]');
  var progressEl = document.querySelector('[data-related-progress]');
  if (!scrollEl) return;

  /* ── STATE ── */
  var isDragging = false;
  var startX = 0;
  var startScrollLeft = 0;
  var velocity = 0;
  var lastX = 0;
  var lastTime = 0;
  var animationFrame = null;

  /* ── PROGRESS BAR ── */
  function updateProgress() {
    if (!progressEl) return;
    var max = scrollEl.scrollWidth - scrollEl.clientWidth;
    var pct = max > 0 ? (scrollEl.scrollLeft / max) * 100 : 0;
    progressEl.style.width = Math.max(20, pct) + '%';
  }

  var ticking = false;
  scrollEl.addEventListener('scroll', function () {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(function () {
        updateProgress();
        ticking = false;
      });
    }
  }, { passive: true });

  /* ── MOUSE DRAG ── */
  scrollEl.addEventListener('mousedown', function (e) {
    isDragging = true;
    startX = e.pageX;
    startScrollLeft = scrollEl.scrollLeft;
    lastX = e.pageX;
    lastTime = Date.now();
    velocity = 0;
    scrollEl.style.cursor = 'grabbing';
    scrollEl.style.userSelect = 'none';
    if (animationFrame) cancelAnimationFrame(animationFrame);
  });

  document.addEventListener('mousemove', function (e) {
    if (!isDragging) return;
    e.preventDefault();
    var dx = e.pageX - startX;
    scrollEl.scrollLeft = startScrollLeft - dx;

    // Track velocity
    var now = Date.now();
    var dt = now - lastTime;
    if (dt > 0) {
      velocity = (e.pageX - lastX) / dt;
    }
    lastX = e.pageX;
    lastTime = now;
  });

  document.addEventListener('mouseup', function () {
    if (!isDragging) return;
    isDragging = false;
    scrollEl.style.cursor = 'grab';
    scrollEl.style.userSelect = '';

    // Momentum scrolling
    if (Math.abs(velocity) > 0.1) {
      var momentumVelocity = velocity * 150;
      var friction = 0.95;

      function momentum() {
        if (Math.abs(momentumVelocity) < 0.5) return;
        scrollEl.scrollLeft -= momentumVelocity * 0.016;
        momentumVelocity *= friction;
        animationFrame = requestAnimationFrame(momentum);
      }
      momentum();
    }
  });

  /* ── TOUCH SUPPORT (native scroll is fine, just update progress) ── */
  scrollEl.addEventListener('touchstart', function () {
    if (animationFrame) cancelAnimationFrame(animationFrame);
  }, { passive: true });

  /* ── PREVENT LINK CLICKS DURING DRAG ── */
  scrollEl.addEventListener('click', function (e) {
    if (Math.abs(startX - (e.pageX || 0)) > 5) {
      e.preventDefault();
    }
  }, true);

  // Initial progress
  updateProgress();

  /* ──────────────────────────────────────────────────────────────────────────
     PERSONALISED ROW

     Two problems this solves, in order.

     1. The row used to be IDENTICAL on all 171 product pages. With no
        collection set on the section, Liquid fell back to
        product.collections.first, and that is `shop` for every product: a
        171-product catch-all sorted A-Z. So every page opened with Alessio,
        Amelie, Araam, Arbour. A sectional recommended a bookcase. Fixed in
        product-related.liquid, which now prefers Shopify's own recommendations
        and otherwise picks the SMALLEST collection the product is in.

     2. It was the same for every SHOPPER. This block re-ranks the row against
        what this visitor has actually been browsing, so two people on the same
        product see a different order.

     The HTML is fetched through the Section Rendering API rather than built
     here, so the card markup lives in exactly one place. Rebuilding it in JS
     would mean a second copy of the wishlist, quantity and rating logic to
     drift out of sync.

     Everything below is best-effort. Any failure leaves the server-rendered
     row exactly as it is, which is already correct after fix 1.
     ────────────────────────────────────────────────────────────────────────── */
  var root = document.querySelector('[data-related-root]');
  if (!root || !window.fetch) return;

  var productId = root.getAttribute('data-product-id');
  var sectionId = root.getAttribute('data-section-id');
  if (!productId || !sectionId) return;

  function readProfile() {
    try {
      var raw = JSON.parse(localStorage.getItem('aykah_browse_profile') || '[]');
      return Array.isArray(raw) ? raw.filter(function (e) { return e && e.h; }) : [];
    } catch (e) {
      return [];
    }
  }

  /* How well one card matches this shopper's browsing.

     Recency is weighted: the thing they looked at last says more about intent
     than the thing they looked at eight products ago. Type is worth more than
     tags because "Sectional" is a stronger signal than a shared "wood" tag,
     and price proximity is worth least because it is the weakest of the three
     on its own. */
  function scoreCard(card, profile) {
    var type = (card.getAttribute('data-p-type') || '').toLowerCase();
    var tags = (card.getAttribute('data-p-tags') || '').toLowerCase().split('|').filter(Boolean);
    var price = parseInt(card.getAttribute('data-p-price'), 10) || 0;
    var handle = card.getAttribute('data-p-handle') || '';
    var score = 0;

    for (var i = 0; i < profile.length; i++) {
      var seen = profile[i];
      var recency = 1 / (i + 1);

      if (seen.h === handle) {
        /* Already viewed. Mildly demoted: it is not new information, and the
           row is meant to widen the choice rather than repeat it. */
        score -= 2 * recency;
        continue;
      }
      if (type && seen.t && type === seen.t) score += 6 * recency;

      var shared = 0;
      var seenTags = seen.g || [];
      for (var t = 0; t < tags.length && shared < 3; t++) {
        if (seenTags.indexOf(tags[t]) !== -1) shared++;
      }
      score += shared * 1.5 * recency;

      if (price > 0 && seen.p > 0) {
        var ratio = price > seen.p ? seen.p / price : price / seen.p;
        if (ratio > 0.6) score += 2 * recency;
      }
    }
    return score;
  }

  function personalise(container) {
    var profile = readProfile();
    if (profile.length < 2) return; /* Too little signal to reorder honestly. */

    var cards = Array.prototype.slice.call(container.querySelectorAll('[data-related-card]'));
    if (cards.length < 3) return;

    var scored = cards.map(function (card, i) {
      return { card: card, score: scoreCard(card, profile), i: i };
    });
    /* Ties keep Shopify's own order, which is the better default. */
    scored.sort(function (a, b) { return b.score - a.score || a.i - b.i; });

    var frag = document.createDocumentFragment();
    scored.forEach(function (s) { frag.appendChild(s.card); });
    container.appendChild(frag);
  }

  var url = '/recommendations/products?section_id=' + encodeURIComponent(sectionId) +
            '&product_id=' + encodeURIComponent(productId) + '&limit=10&intent=related';

  fetch(url, { credentials: 'same-origin' })
    .then(function (res) { return res.ok ? res.text() : null; })
    .then(function (html) {
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-related-scroll]');
      /* No cards back means Shopify had no recommendations for this product.
         Keep the server-rendered collection row rather than emptying the rail. */
      if (!fresh || !fresh.querySelector('[data-related-card]')) return;

      scrollEl.innerHTML = fresh.innerHTML;
      personalise(scrollEl);

      /* The card scripts delegate their clicks from document, so the new cards
         are already live. These two do run per element on load, so they need a
         nudge: the filled/empty heart state, and the quantity-rule map the cart
         drawer reads because /cart.js carries no metafields. */
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
      if (typeof window.AykahSeedQtyRules === 'function') window.AykahSeedQtyRules();

      scrollEl.scrollLeft = 0;
      updateProgress();
    })
    .catch(function () { /* Server-rendered row stands. */ });
})();
